import NotificationSettingsForm from "@src/components/forms/NotificationSettingsForm";
import { useEffect, useState } from "react";
import { useOperatorContext } from "@src/context/operatorContext";
import { BackButton, Button, Icon } from "@src/components/atoms";
import OperatorHooks from "@src/context/hooks/operatorHooks";

const NotificationSettingsFlow = (props) => {
  const { profile } = useOperatorContext();
  const { appUser, customer, operator, operatorMetadata } = profile;

  const { setOpen, mutationCallback } = props;

  const userNotifData = operatorMetadata.reduce((acc, val) => {
    if (val.key.includes("opt_in")) {
      acc[val.key] = val.value === "true";
    }
    return acc;
  }, {});

  const [step, setStep] = useState("form");
  const [formData, setFormData] = useState(null);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    const autoCloseSteps = [
      "conclusion_success",
      "conclusion_feedback",
      "conclusion_retain",
    ];

    if (autoCloseSteps.includes(step)) {
      const timer = setTimeout(() => {
        setFormData(null);
        setOpen(false);
      }, 2000);

      return () => clearTimeout(timer); // cleanup on unmount or step change
    }
  }, [step, setOpen]);

  const profileMutation =
    OperatorHooks.useSubmitOperatorProfile(mutationCallback);
  const feedbackMutation = OperatorHooks.useSubmitOperatorProfile(); // no callback

  const handleSubmit = async (data) => {
    setFormData(data);
    //check if any changes made
    const noChanges = Object.keys(data).every(
      (key) => data[key] === userNotifData[key],
    );
    if (noChanges) {
      setStep("conclusion_success");
      return;
    }
    const emailPrefs = [
      data.opt_in_accepted_level_2, // performance
      data.opt_in_accepted_level_3, // updates
    ];
    const pushPref = data.opt_in_accepted_level_4; // insights
    const securityPref = data.opt_in_accepted_level_1; //security - should be always true

    const optedOutEmail = !emailPrefs[0] || !emailPrefs[1];
    const optedOutAllEmail = !emailPrefs[0] && !emailPrefs[1];
    const optedOutPush = !pushPref;
    const optedInAll =
      securityPref && emailPrefs[0] && emailPrefs[1] && pushPref;

    //compare opt-in/opt-out
    const onlyOptedIn = Object.keys(data).every((key) => {
      const initial = userNotifData[key];
      const current = data[key];
      return initial === current || (initial === false && current === true);
    });

    if (optedInAll || onlyOptedIn) {
      handleConfirm(data);
      setStep("conclusion_success");
    } else if (optedOutAllEmail) {
      setStep("message1_all_email");
    } else if (optedOutPush && !optedOutEmail) {
      setStep("message2_push_only");
    } else if (optedOutEmail || optedOutPush) {
      setStep("message1_partial_email");
    }
  };

  const handleConfirm = async (data) => {
    const isEvent = data?.nativeEvent && data?.target;
    const dataToUse = isEvent ? formData : data || formData;

    if (!dataToUse) {
      console.warn("No valid form data available for submission.");
      return;
    }
    //prep request body
    const request = {
      operatorCode: operator.code,
      email: appUser.email,
      firstname: appUser.firstname,
      lastname: appUser.lastname,
      metadata: [
        {
          key: "opt_in_accepted_level_1",
          value: String(true),
          type: "BOOLEAN",
        },
        {
          key: "opt_in_accepted_level_2",
          value: String(dataToUse.opt_in_accepted_level_2),
          type: "BOOLEAN",
        },
        {
          key: "opt_in_accepted_level_3",
          value: String(dataToUse.opt_in_accepted_level_3),
          type: "BOOLEAN",
        },
        {
          key: "opt_in_accepted_level_4",
          value: String(dataToUse.opt_in_accepted_level_4),
          type: "BOOLEAN",
        },
      ],
    };
    profileMutation.mutateAsync(request);
    setStep("feedback");
  };

  const handleKeepInsights = () => {
    setStep("conclusion_retain");
  };

  const handleFeedbackSubmit = async () => {
    try {
      // send request but no callback to update profile data
      const request = {
        operatorCode: operator.code,
        email: appUser.email,
        firstname: appUser.firstname,
        lastname: appUser.lastname,
        metadata: [
          {
            key: "opt_out_reason",
            value: feedback,
            type: "STRING",
          },
        ],
      };
      feedbackMutation.mutateAsync(request);
      setStep("conclusion_feedback");
    } catch (error) {
      console.log("Error saving the metadata", error);
      setStep("error");
    }
  };

  return (
    <div className="mx-auto p-6 h-[600px]">
      {step === "form" && (
        <NotificationSettingsForm
          title={"Notification settings"}
          onSubmit={handleSubmit}
          industry={customer.industry}
          defaultValues={formData || userNotifData}
        />
      )}

      {/*  */}
      {step.includes("message") && (
        <div className="flex flex-col justify-between w-full h-full align-center text-center">
          <div className="text-2xl font-extrabold">Are you sure?</div>
          {step.includes("email") ? (
            <div>
              Most{" "}
              <span className="capitalize font-bold text-primary-500">
                {customer.name}
              </span>{" "}
              users continue to receive email notifications.
            </div>
          ) : (
            <div>
              Signol will only occasionally notify you with personalised and
              timely insights, respecting your busy workload.
            </div>
          )}

          <div className="relative w-full">
            <div className="absolute left-0 bottom-2 p-1">
              <BackButton onCustomBack={() => setStep("form")} noContainer />
            </div>
            <div className="mx-8">
              <Button
                secondary
                onClick={handleConfirm}
                className="w-28 rounded-lg text-base border border-transparent px-4 py-2 font-bold text-white    transition ease-in-out duration-100"
              >
                I’m sure I still want to opt out
              </Button>
              <Button
                onClick={handleKeepInsights}
                className="w-28 rounded-lg text-base border border-transparent px-4 py-2  font-bold text-white   transition ease-in-out duration-100"
              >
                I'd like to get these insights
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === "feedback" && (
        <div className="flex flex-col justify-between w-full h-full text-center">
          <div className="flex flex-col justify-center flex-grow p-auto">
            <div className="text-2xl font-extrabold py-4">
              Update successful.
            </div>
            <div className="text-gray-700 p-4 text-base font-medium mb-8">
              Your notification preferences have been updated.
              <span className="font-bold">
                {" "}
                If you have a moment, let us know how we can improve.
              </span>
            </div>
            <textarea
              className="data-hj-allow p-2 border rounded-lg block w-full h-32 text-xs sm:text-sm leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-primary-300"
              placeholder="Your feedback..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
          <div className="flex justify-center items-center pt-4 md:pt-6 w-full relative">
            <div className="absolute left-0 bottom-2 p-1">
              <BackButton onCustomBack={() => setStep("form")} noContainer />
            </div>
            <div className="flex justify-center">
              <Button
                disabled={feedback.length === 0}
                onClick={handleFeedbackSubmit}
              >
                Submit Feedback
              </Button>
            </div>
          </div>
        </div>
      )}

      {step.includes("conclusion") && (
        <div className="relative flex flex-col justify-between w-full h-full">
          {step.includes("feedback") ? (
            <div className="flex items-center justify-center h-full p-8 text-center">
              <div className="text-gray-900 text-2xl font-extrabold">
                We appreciate your feedback - we'll work on it.
              </div>
            </div>
          ) : step.includes("retain") ? (
            <div className="flex items-center justify-center h-full p-8 text-center">
              <div className="text-gray-900 text-2xl font-extrabold">
                Great choice, we'll only send you the most relevant updates.
              </div>
            </div>
          ) : (
            <>
              <div className="absolute left-0 bottom-2 p-1">
                <BackButton onCustomBack={() => setStep("form")} noContainer />
              </div>
              <div className="flex flex-col h-full w-full justify-center gap-8 text-center">
                <div className="text-2xl font-extrabold py-2">Thanks.</div>
                <div className="text-base font-medium p-6">
                  Your notification preferences have been updated.
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {step.includes("error") && (
        <div className="flex flex-col justify-between w-full h-full text-center">
          <div className="flex flex-col justify-center flex-grow p-auto">
            <div className="text-2xl font-extrabold py-4">Update failed.</div>
            <div className="text-gray-700 p-4 text-base font-medium mb-8">
              We couldn't save your changes at this moment.
              <span className="font-bold"> Please try again later.</span>
            </div>
          </div>
          <div className="flex justify-center items-center pt-4 md:pt-6 w-full relative">
            <div className="flex justify-center">
              <Button
                onClick={() => {
                  setFormData(null);
                  setOpen(false);
                }}
              >
                Exit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettingsFlow;
